/**
 * Popow CRM Provider
 *
 * Popow CRM 백엔드로 이벤트 전송 및 사용자 추적
 * 캠페인 자동화와 사용자 추적을 위해 사용
 */
import type { AnalyticsTracker } from '../AnalyticsTracker';

// ============================================================================
// Configuration
// ============================================================================

const POPOW_API_URL = 'https://crm-backend-gbhojycnbq-uc.a.run.app/api';
const WORKSPACE_ID = 'ws_roundsquare';

// ============================================================================
// PopowCrmProvider (AnalyticsTracker 구현체)
// ============================================================================

/**
 * Popow CRM Analytics Tracker
 *
 * 🎯 Email 우선 정책:
 * - identify 이후 email이 확인된 이벤트만 distinctId를 email로 사용
 * - email이 없거나 확인되지 않은 경우 원래 distinctId 사용
 */
export class PopowCrmProvider implements AnalyticsTracker {
  // 🎯 Singleton: distinctId를 static으로 저장 (모든 인스턴스 공유)
  private static sharedDistinctId: string | null = null;
  private static sharedEmailVerified: boolean = false;

  // 🎯 MixpanelProvider 참조 (getDistinctId용)
  private mixpanelProvider: AnalyticsTracker | null = null;

  // 🎯 이벤트 배치 처리
  private eventQueue: Array<{
    distinctId: string;
    name: string;
    properties: Record<string, unknown>;
  }> = [];
  private flushTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly BATCH_SIZE = 5;
  private readonly FLUSH_INTERVAL_MS = 5000;
  private isBeaconListenerRegistered = false;

  /**
   * MixpanelProvider 참조 설정 (getDistinctId 연동용)
   */
  setMixpanelProvider(provider: AnalyticsTracker): void {
    this.mixpanelProvider = provider;
  }

  private get distinctId(): string | null {
    return PopowCrmProvider.sharedDistinctId;
  }

  private set distinctId(value: string | null) {
    PopowCrmProvider.sharedDistinctId = value;
  }

  private get emailVerified(): boolean {
    return PopowCrmProvider.sharedEmailVerified;
  }

  private set emailVerified(value: boolean) {
    PopowCrmProvider.sharedEmailVerified = value;
  }

  // ============================================================================
  // Helpers
  // ============================================================================

  private isValidEmail(email: string | null | undefined): boolean {
    if (!email || typeof email !== 'string') {
      return false;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private extractEmail(properties: Record<string, unknown>): string | null {
    const email = (properties?.$email || properties?.email) as string | undefined;
    return this.isValidEmail(email) ? email! : null;
  }

  // ============================================================================
  // AnalyticsTracker Interface
  // ============================================================================

  async identify(userId: string): Promise<void> {
    this.distinctId = userId;

    fetch(`${POPOW_API_URL}/events/identify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        workspaceId: WORKSPACE_ID,
        distinctId: userId,
      }),
    }).catch(err => {
      console.error('[Popow] Identify failed:', err);
    });
  }

  async getDistinctId(): Promise<string | null> {
    if (this.mixpanelProvider?.getDistinctId) {
      return this.mixpanelProvider.getDistinctId();
    }
    return this.distinctId;
  }

  async setUserProperties(properties: Record<string, unknown>): Promise<void> {
    if (!this.distinctId) {
      return;
    }

    fetch(`${POPOW_API_URL}/events/identify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        workspaceId: WORKSPACE_ID,
        distinctId: this.distinctId,
        properties,
      }),
    }).catch(err => {
      console.error('[Popow] setUserProperties failed:', err);
    });
  }

  async setUser(userId: string, properties: Record<string, unknown>): Promise<void> {
    // 🎯 email 우선 정책
    const email = this.extractEmail(properties);
    const finalDistinctId = email || userId;

    if (email) {
      this.emailVerified = true;
    } else {
      this.emailVerified = false;
    }

    this.distinctId = finalDistinctId;

    // 🎯 Mixpanel의 현재 distinctId를 가져와서 previousId로 사용 (익명 기간 이벤트 병합)
    let previousId: string | undefined;
    try {
      const mixpanelDistinctId = await this.getDistinctId();
      if (mixpanelDistinctId && mixpanelDistinctId !== finalDistinctId) {
        previousId = mixpanelDistinctId;
      }
    } catch {
      // ignore
    }

    fetch(`${POPOW_API_URL}/events/identify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        workspaceId: WORKSPACE_ID,
        distinctId: finalDistinctId,
        ...(previousId && { previousId }),
        properties,
      }),
    }).catch(err => {
      console.error('[Popow] setUser failed:', err);
    });
  }

  track(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.distinctId) {
      return;
    }

    // 🎯 email이 확인되었고, properties에 email이 있으면 email을 distinctId로 사용
    let finalDistinctId = this.distinctId;
    if (this.emailVerified && properties) {
      const email = this.extractEmail(properties);
      if (email) {
        finalDistinctId = email;
      }
    }

    // 🎯 이벤트를 큐에 추가 (배치 처리)
    this.eventQueue.push({
      distinctId: finalDistinctId,
      name: eventName,
      properties: properties ?? {},
    });

    // beforeunload 리스너 등록 (최초 1회)
    this.registerBeaconListener();

    if (this.eventQueue.length >= this.BATCH_SIZE) {
      this.flush();
    } else if (!this.flushTimeoutId) {
      this.flushTimeoutId = setTimeout(() => this.flush(), this.FLUSH_INTERVAL_MS);
    }
  }

  /**
   * 큐에 쌓인 이벤트를 일괄 전송
   */
  private flush(): void {
    if (this.flushTimeoutId) {
      clearTimeout(this.flushTimeoutId);
      this.flushTimeoutId = null;
    }

    if (this.eventQueue.length === 0) {
      return;
    }

    const events = [...this.eventQueue];
    this.eventQueue = [];

    // 각 이벤트를 병렬로 전송 (서버가 배치 API를 지원하지 않으므로 개별 전송)
    for (const event of events) {
      fetch(`${POPOW_API_URL}/events/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspaceId: WORKSPACE_ID,
          event: {
            distinctId: event.distinctId,
            name: event.name,
            properties: event.properties,
          },
        }),
      }).catch(err => {
        console.error('[Popow] Track failed:', event.name, err);
      });
    }
  }

  /**
   * 페이지 언로드 시 남은 큐를 sendBeacon으로 전송
   */
  private registerBeaconListener(): void {
    if (this.isBeaconListenerRegistered || typeof window === 'undefined') {
      return;
    }
    this.isBeaconListenerRegistered = true;

    window.addEventListener('beforeunload', () => {
      if (this.flushTimeoutId) {
        clearTimeout(this.flushTimeoutId);
        this.flushTimeoutId = null;
      }

      if (this.eventQueue.length === 0) {
        return;
      }

      const events = [...this.eventQueue];
      this.eventQueue = [];

      // sendBeacon으로 각 이벤트 전송 (beforeunload에서도 안정적)
      for (const event of events) {
        const payload = JSON.stringify({
          workspaceId: WORKSPACE_ID,
          event: {
            distinctId: event.distinctId,
            name: event.name,
            properties: event.properties,
          },
        });

        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            `${POPOW_API_URL}/events/track`,
            new Blob([payload], { type: 'application/json' }),
          );
        }
      }
    });
  }

  clearUser(): void {
    this.distinctId = null;
    this.emailVerified = false;
  }
}

// 📊 Admin Event tracking is now handled server-side via DB trigger
// See: supabase/migrations/20260219000000_submission_review_tracking_trigger.sql
